import { useMemo } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import Modal from '@/components/common/modal';
import EventCatalogProductDetails from '@/components/events/event-catalog/event-catalog-product-details.tsx';
import type { ProductKey } from '@/types/dtos/orders/response/product.dto.ts';
import { catalogUsesDisplayFeatureFlags, getOrderedEventCatalogKeysForUi } from '@/constants/product-catalog.ts';
import { useCatalogDisplayFeatureAccess, useFeatureAccess } from '@/hooks/use-feature-access';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import { CircularProgress } from '@mui/material';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders.ts';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import { getOriginEventId } from '@/helpers/event.helper';
import { filterProductKeysByCommercialFeatureKeys } from '@/helpers/commercial-feature-product-filter';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

const EventCatalogProductsListModal = ({
  open,
  onModalClose,
  event,
  expandedProductKey,
  onCheckoutRequested,
  commercialFeatureKeysFilter,
}: {
  open: boolean;
  onModalClose: () => void;
  event?: EventDto;
  expandedProductKey?: ProductKey;
  onCheckoutRequested?: () => void;
  /** When set, only products granting at least one of these Event commercial feature keys are shown. */
  commercialFeatureKeysFilter?: string[];
}) => {
  const t = useTranslations();
  const isWideScreen = useBreakpointUp('sm');
  const { hasAccess: hasCatalogOrderAccess, isLoading: catalogOrderAccessLoading } =
    useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);
  const { data } = useGetEventOrdersQuery(getOriginEventId(event));
  const storeProducts = useProductsStore((s) => s.products);
  const { featureAccess, isLoading: catalogFlagsLoading } = useCatalogDisplayFeatureAccess();
  const gateCatalogFlags = catalogUsesDisplayFeatureFlags();
  const visibleCatalogKeys = useMemo(() => {
    const keys = getOrderedEventCatalogKeysForUi(storeProducts, { featureAccess });
    return filterProductKeysByCommercialFeatureKeys(keys, storeProducts ?? [], commercialFeatureKeysFilter);
  }, [storeProducts, featureAccess, commercialFeatureKeysFilter]);

  if (catalogOrderAccessLoading || !hasCatalogOrderAccess) {
    return null;
  }

  return (
    <Modal
      open={open}
      size={isWideScreen ? 'md' : 'sm'}
      data-testid="event-catalog-products-list-modal"
      handleClose={onModalClose}
      title={t('common.features')}
      description={t('event.features.modalDescription')}
    >
      {!data || (gateCatalogFlags && catalogFlagsLoading) ? (
        <CircularProgress />
      ) : (
        <>
          {visibleCatalogKeys.map((productKey) => (
            <EventCatalogProductDetails
              key={productKey}
              event={event}
              orders={data}
              productKey={productKey}
              expanded={expandedProductKey === productKey}
              onCheckoutRequested={onCheckoutRequested}
            />
          ))}
        </>
      )}
    </Modal>
  );
};

export default EventCatalogProductsListModal;
